import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@codelittinc/carbon-design-system";
import { celebrateTrack } from "./celebration";
import type {
  CompletionResponse,
  TrainingError,
  TrainingItem,
  TrainingResponse,
  TrainingTrack,
  VideoProgress,
} from "@carbon-os/server/contract";
import { trackTraining } from "./analytics";
import { browserDeps, type LoadDeps } from "./api";
import {
  beginSignIn,
  clearSignInAttempt,
  isRedirecting,
  shouldRedirect,
  TRAINING_SIGNIN_ATTEMPT_KEY,
} from "./signin";

/** When each material was marked done, or null; one entry however many tracks share it. */
export type Done = Record<string, string | null>;

export type TrainingState =
  | { kind: "loading" }
  /** `viewerKey` is the server's opaque key for the signed-in person, for state kept in this browser. */
  | { kind: "ready"; tracks: TrainingTrack[]; done: Done; viewerKey: string | null }
  /** A 401. Only /training acts on it: Home leaves sign-in to the tool list. */
  | { kind: "needs_signin" }
  | { kind: "signin_failed" }
  | { kind: "wrong_account"; signedInAs: string | null; signOutUrl: string }
  | { kind: "not_in_gatekeeper"; signedInAs: string | null; signOutUrl: string }
  /** `failures` counts this visit's failed attempts; `retrying` keeps the panel up during Try again. */
  | { kind: "unavailable"; failures: number; retrying: boolean }
  | { kind: "not_connected"; message: string };

type Outcome = Exclude<TrainingState, { kind: "unavailable" | "signin_failed" }> | { kind: "unavailable" };

export const SAVE_FAILED = "We couldn't save that. Try again.";
export const QUIZ_REQUIRED = "Passing the quiz marks this done.";
export const MARKED_DONE = "Marked as done";
export const TRACK_EXPIRED = "This track has ended, so this can't be saved. Ask an admin if you need more time.";

/** End dates are days in New York (carbon-gatekeeper#201), so they are shown and counted there. */
const TRACK_TIME_ZONE = "America/New_York";

/** "Nov 12, 2026", the end day as Gatekeeper set it. */
export function formatEndDay(endsAt: string): string {
  return new Date(endsAt).toLocaleDateString("en-US", { timeZone: TRACK_TIME_ZONE, month: "short", day: "numeric", year: "numeric" });
}

const NEW_YORK_DAY = new Intl.DateTimeFormat("en-CA", { timeZone: TRACK_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" });

function dayNumber(instant: Date): number {
  const [y, m, d] = NEW_YORK_DAY
    .format(instant)
    .split("-")
    .map(Number);
  return Date.UTC(y!, m! - 1, d!) / 86_400_000;
}

/**
 * Past its end, by Gatekeeper's last word or by the clock: a page left open
 * over the deadline says so before the next save is refused.
 */
export function trackEnded(track: TrainingTrack, now: Date = new Date()): boolean {
  return track.locked || (track.endsAt !== null && now.getTime() > Date.parse(track.endsAt));
}

/** "Due today", "Due tomorrow", "Due in 3 days": whole New York days to the end day. Not for an ended track. */
export function dueLabel(endsAt: string, now: Date = new Date()): string {
  const left = dayNumber(new Date(endsAt)) - dayNumber(now);
  if (left <= 0) return "Due today";
  return left === 1 ? "Due tomorrow" : `Due in ${left} days`;
}

/** A completion counts for a track only if it was made by the track's end. */
export function countsFor(track: TrainingTrack, completedAt: string | null | undefined): boolean {
  if (!completedAt) return false;
  // A tick still saving has no time yet ("now"): it is made now, so it counts
  // for a track that is still open and not for one that has ended.
  const parsed = Date.parse(completedAt);
  const at = Number.isNaN(parsed) ? Date.now() : parsed;
  return track.endsAt === null || at <= Date.parse(track.endsAt);
}

/**
 * GET /api/training. As with the tool list, only a 200 can produce no tracks;
 * every other answer, and a request that never arrives, is an error state.
 */
export async function loadTraining(deps: Pick<LoadDeps, "fetch">): Promise<Outcome> {
  let res: Response;
  try {
    res = await deps.fetch("/api/training", {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
  } catch {
    return { kind: "unavailable" };
  }

  if (res.status === 200) {
    const data = (await res.json().catch(() => null)) as TrainingResponse | null;
    if (!data || !Array.isArray(data.tracks)) return { kind: "unavailable" };
    return {
      kind: "ready",
      tracks: data.tracks,
      done: doneOf(data.tracks),
      viewerKey: typeof data.viewerKey === "string" && data.viewerKey ? data.viewerKey : null,
    };
  }

  const body = (await res.json().catch(() => ({}))) as Partial<TrainingError>;

  if (res.status === 401) return { kind: "needs_signin" };
  if (res.status === 403 && body.code === "not_in_gatekeeper") {
    return { kind: "not_in_gatekeeper", signedInAs: body.signedInAs ?? null, signOutUrl: body.signOutUrl ?? "/auth/signout" };
  }
  if (res.status === 403) {
    return {
      kind: "wrong_account",
      signedInAs: body.signedInAs ?? null,
      signOutUrl: body.signOutUrl ?? "/auth/signout",
    };
  }
  if (res.status === 503 && body.code === "not_connected") {
    return { kind: "not_connected", message: body.error ?? "" };
  }
  return { kind: "unavailable" };
}

export function doneOf(tracks: TrainingTrack[]): Done {
  const done: Done = {};
  for (const track of tracks) {
    for (const item of track.items) done[item.materialId] = item.completedAt;
  }
  return done;
}

/**
 * Counted from the items, never taken from Gatekeeper, so a tick shows at once.
 * An ended track counts only what was done by its end, as Gatekeeper does.
 */
export function progressOf(track: TrainingTrack, done: Done): { done: number; total: number } {
  return {
    done: track.items.filter((item) => countsFor(track, done[item.materialId])).length,
    total: track.items.length,
  };
}

export function isComplete(track: TrainingTrack, done: Done): boolean {
  const progress = progressOf(track, done);
  return progress.total > 0 && progress.done === progress.total;
}

/** The first unfinished item it can still be done, or null when the track is done or has ended. */
export function upNext(track: TrainingTrack, done: Done): string | null {
  if (track.locked) return null;
  return track.items.find((item) => !done[item.materialId] && !item.locked)?.materialId ?? null;
}

/** The complete tracks that hold this material: the ones its tick can have just finished. */
export function completedBy(tracks: TrainingTrack[], materialId: string, done: Done): TrainingTrack[] {
  return tracks.filter(
    (track) => track.items.some((item) => item.materialId === materialId) && isComplete(track, done),
  );
}

/** The tracks holding this material that `after` completes and `before` did not: the ones to celebrate. */
export function newlyCompleted(tracks: TrainingTrack[], materialId: string, before: Done, after: Done): TrainingTrack[] {
  return completedBy(tracks, materialId, after).filter((track) => !isComplete(track, before));
}

/**
 * The one "Track complete" toast, with its confetti and chime, whether a tick,
 * a quiz pass, a video or a process finished the track.
 */
export function celebrate(track: TrainingTrack): void {
  toast.success(`Track complete: ${track.name}`, { description: `Nice work. That's all ${track.items.length}.` });
  celebrateTrack(track.name);
}

export function firstUnfinished(tracks: TrainingTrack[], done: Done): TrainingTrack | null {
  return tracks.find((track) => upNext(track, done) !== null) ?? null;
}

/**
 * Across every track, each material counted once, so a shared one is one tick.
 * It counts when it counts in at least one track holding it, as `progressOf` does.
 */
export function overall(tracks: TrainingTrack[], done: Done): { done: number; total: number } {
  const materials = new Set<string>();
  const counted = new Set<string>();
  for (const track of tracks) {
    for (const item of track.items) {
      materials.add(item.materialId);
      if (countsFor(track, done[item.materialId])) counted.add(item.materialId);
    }
  }
  return { done: counted.size, total: materials.size };
}

/** Anything done, watched, or attempted in any track: "Continue" rather than "Start". */
export function hasStarted(tracks: TrainingTrack[], done: Done): boolean {
  return tracks.some((track) =>
    track.items.some(
      (item) =>
        countsFor(track, done[item.materialId]) ||
        (item.video?.watchedPercent ?? 0) > 0 ||
        (item.quiz !== null && (item.quiz.attempts > 0 || item.quiz.inProgress !== null)),
    ),
  );
}

/** `code` is the error body's, when there is one. */
export type Failed = { ok: false; status: number; code?: string };

/** A refusal's status and code, from a response that is not a 200. */
export async function failedOf(res: Response): Promise<Failed> {
  const body = (await res.json().catch(() => null)) as Partial<TrainingError> | null;
  return typeof body?.code === "string" ? { ok: false, status: res.status, code: body.code } : { ok: false, status: res.status };
}

/** PUT marks done, DELETE undoes. `status` is 0 when the request never arrived. */
export async function saveCompletion(
  fetchImpl: typeof fetch,
  materialId: string,
  done: boolean,
): Promise<{ ok: true; completedAt: string | null } | Failed> {
  let res: Response;
  try {
    res = await fetchImpl(`/api/training/completions/${encodeURIComponent(materialId)}`, {
      method: done ? "PUT" : "DELETE",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
  } catch {
    return { ok: false, status: 0 };
  }
  if (res.status !== 200) return failedOf(res);
  const body = (await res.json().catch(() => null)) as CompletionResponse | null;
  if (!body) return { ok: false, status: res.status };
  return { ok: true, completedAt: body.completedAt };
}

/**
 * A progress answer laid over the list: the item's watched percent, and done
 * the first time `completedAt` comes for an item with no quiz (passing the
 * quiz is the only way for one with). `completed` says this answer marked it.
 */
export function withProgress(
  tracks: TrainingTrack[],
  done: Done,
  materialId: string,
  progress: VideoProgress,
): { tracks: TrainingTrack[]; done: Done; completed: boolean } {
  let quizzed = false;
  const next = tracks.map((track) => ({
    ...track,
    items: track.items.map((item) => {
      if (item.materialId !== materialId) return item;
      if (item.quiz) quizzed = true;
      return item.video
        ? {
            ...item,
            video: {
              ...item.video,
              watchedPercent: progress.watchedPercent,
              lastPositionSeconds: progress.lastPositionSeconds,
            },
          }
        : item;
    }),
  }));
  const completed = !quizzed && progress.completedAt !== null && !done[materialId];
  return { tracks: next, done: completed ? { ...done, [materialId]: progress.completedAt } : done, completed };
}

export interface TrainingControls {
  state: TrainingState;
  retry: () => void;
  /** Flips one material, in every track that has it, then saves it. */
  toggle: (materialId: string, trackId: string) => void;
  /** Runs the sign-in loop guard for a `needs_signin` state. */
  signIn: () => void;
  /**
   * Marks a material done because its quiz was passed at `percent`, with its
   * quiz summary to match, then re-reads quietly. Returns the tracks the pass
   * finished, so each gets its toast. Never counts as a tick:
   * `training_item_completed` stays the manual one.
   */
  passed: (materialId: string, completedAt: string, percent: number) => TrainingTrack[];
  /**
   * Marks every step holding this Company handbook process done, in every
   * track, because its current version was just acknowledged; then re-reads
   * quietly. Returns the tracks it finished, so each gets its celebration.
   */
  acknowledged: (processId: string, completedAt: string) => TrainingTrack[];
  /** Re-reads the list quietly, so a row shows what Gatekeeper now says; after any tick still saving. */
  refresh: () => void;
  /**
   * Gatekeeper's answer to a video progress report: the row's watched percent
   * moves at once, and the first `completedAt` marks the item done, as a tick
   * would. Never for a material with a quiz: passing it is what marks that.
   */
  watched: (materialId: string, trackId: string, progress: VideoProgress, viewerKey: string | null) => void;
}

/** One material's saves: at most one request out, and the latest wish waiting behind it. */
interface Queue {
  sending: boolean;
  desired: boolean;
  /** What Gatekeeper last confirmed; a failed save goes back to it. */
  confirmed: string | null;
}

export const fetchNow: typeof fetch = (...args) => browserDeps().fetch(...args);

export function useTraining(): TrainingControls {
  const [state, setState] = useState<TrainingState>({ kind: "loading" });
  // Saves and the quiet refresh finish long after the render that started
  // them, so they read and write the state through this, never a stale closure.
  const latest = useRef<TrainingState>(state);
  const failures = useRef(0);
  const inFlight = useRef(false);
  const started = useRef(false);
  const queues = useRef(new Map<string, Queue>());
  // Bumped by every tick and every load: a refresh that started before either
  // would put back what the person has just changed.
  const generation = useRef(0);
  // A re-read was asked for while a tick was saving; the last save to settle owes it.
  const owed = useRef(false);
  /** One map per re-read that is out, collecting the progress answers that arrive meanwhile. */
  const progressDuring = useRef(new Set<Map<string, VideoProgress>>());

  const set = useCallback((next: TrainingState) => {
    latest.current = next;
    setState(next);
  }, []);

  const setDone = useCallback(
    (materialId: string, completedAt: string | null) => {
      const current = latest.current;
      if (current.kind !== "ready") return;
      set({ ...current, done: { ...current.done, [materialId]: completedAt } });
    },
    [set],
  );

  const load = useCallback(async () => {
    inFlight.current = true;
    generation.current += 1;
    const outcome = await loadTraining({ fetch: fetchNow });
    inFlight.current = false;
    if (outcome.kind === "unavailable") {
      failures.current += 1;
      set({ kind: "unavailable", failures: failures.current, retrying: false });
    } else {
      if (outcome.kind === "ready") clearSignInAttempt(window.sessionStorage, TRAINING_SIGNIN_ATTEMPT_KEY);
      set(outcome);
    }
  }, [set]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  const retry = useCallback(() => {
    if (inFlight.current) return;
    const current = latest.current;
    if (current.kind === "unavailable") set({ ...current, retrying: true });
    void load();
  }, [load, set]);

  /**
   * Re-reads the list once every save has settled, so the screen matches
   * Gatekeeper. Quiet: no skeleton, and a failure or a 401 keeps what is shown.
   */
  const refresh = useCallback(async () => {
    owed.current = false;
    const at = generation.current;
    const viewerAtStart = latest.current.kind === "ready" ? latest.current.viewerKey : null;
    // Progress answers keep coming while this is out, every 15 s or so; they
    // are laid over what it brings back instead of cancelling it.
    const during = new Map<string, VideoProgress>();
    progressDuring.current.add(during);
    const outcome = await loadTraining({ fetch: fetchNow });
    progressDuring.current.delete(during);
    if (generation.current !== at || outcome.kind !== "ready" || latest.current.kind !== "ready") return;
    if (latest.current.viewerKey !== viewerAtStart) return;
    let next = outcome;
    for (const [materialId, progress] of outcome.viewerKey === viewerAtStart ? during : []) {
      const applied = withProgress(next.tracks, next.done, materialId, progress);
      next = { ...next, tracks: applied.tracks, done: applied.done };
    }
    set(next);
  }, [set]);

  /** A re-read from outside the save queues: it would undo a tick still saving, so it waits for the last one. */
  const refreshWhenSettled = useCallback(() => {
    if (queues.current.size > 0) owed.current = true;
    else void refresh();
  }, [refresh]);

  const drain = useCallback(
    async (materialId: string, trackId: string) => {
      const queue = queues.current.get(materialId);
      if (!queue) return;
      queue.sending = true;
      let sent: boolean;
      let saved: Awaited<ReturnType<typeof saveCompletion>>;
      do {
        sent = queue.desired;
        saved = await saveCompletion(fetchNow, materialId, sent);
        if (saved.ok) {
          queue.confirmed = saved.completedAt;
          trackTraining(sent ? "training_item_completed" : "training_item_uncompleted", {
            material_id: materialId,
            track_id: trackId,
          });
        }
        // Ticked again while that was out: send the latest wish too.
      } while (saved.ok && queue.desired !== sent);

      queues.current.delete(materialId);
      setDone(materialId, queue.confirmed);

      if (!saved.ok) {
        // A quiz was published since the list was read: the tick is already undone.
        if (saved.code === "quiz_required") {
          toast.error(QUIZ_REQUIRED);
          refreshWhenSettled();
          return;
        }
        // The end date passed while the page was open: re-read, so the row locks.
        if (saved.code === "track_expired") {
          toast.error(TRACK_EXPIRED);
          refreshWhenSettled();
          return;
        }
        toast.error(SAVE_FAILED);
        // The material is gone, or the session is: either way the list is stale.
        if (saved.status === 404 || saved.status === 401) retry();
        else if (owed.current && queues.current.size === 0) void refresh();
        return;
      }

      const current = latest.current;
      if (sent && current.kind === "ready") {
        // Every track holding the material, not only the one it was ticked in.
        for (const track of completedBy(current.tracks, materialId, current.done)) {
          trackTraining("training_track_completed", { track_id: track.id, item_count: track.items.length });
        }
      }
      if (queues.current.size === 0) void refresh();
    },
    [refresh, refreshWhenSettled, retry, setDone],
  );

  const toggle = useCallback(
    (materialId: string, trackId: string) => {
      const current = latest.current;
      if (current.kind !== "ready") return;
      const was = current.done[materialId] ?? null;
      const queue = queues.current.get(materialId) ?? { sending: false, desired: was !== null, confirmed: was };
      queue.desired = was === null;
      queues.current.set(materialId, queue);
      generation.current += 1;
      // Straight away, before the save: every track reads the same entry.
      setDone(materialId, queue.desired ? (queue.confirmed ?? new Date().toISOString()) : null);
      if (!queue.sending) void drain(materialId, trackId);
    },
    [drain, setDone],
  );

  const passed = useCallback(
    (materialId: string, completedAt: string, percent: number) => {
      const current = latest.current;
      if (current.kind !== "ready") return [];
      generation.current += 1;
      const before = current.done;
      const done = { ...before, [materialId]: completedAt };
      // The row reads "Passed · best X%" now, not once the re-read is back.
      const tracks = current.tracks.map((track) => ({
        ...track,
        items: track.items.map((item) =>
          item.materialId === materialId && item.quiz
            ? {
                ...item,
                quiz: {
                  ...item.quiz,
                  bestPercent: Math.max(item.quiz.bestPercent ?? 0, percent),
                  attempts: item.quiz.attempts + 1,
                  inProgress: null,
                },
              }
            : item,
        ),
      }));
      set({ ...current, tracks, done });
      const finished = newlyCompleted(tracks, materialId, before, done);
      for (const track of finished) {
        trackTraining("training_track_completed", { track_id: track.id, item_count: track.items.length });
      }
      refreshWhenSettled();
      return finished;
    },
    [refreshWhenSettled, set],
  );

  const acknowledged = useCallback(
    (processId: string, completedAt: string) => {
      const current = latest.current;
      if (current.kind !== "ready") return [];
      generation.current += 1;
      const before = current.done;
      const done = { ...before };
      // One process can be a step in several tracks, each step its own id.
      const tracks = current.tracks.map((track) => ({
        ...track,
        items: track.items.map((item) => {
          // An ended track's step can't be acknowledged, from Training or a Handbook read.
          if (item.itemType !== "process" || (item.processId ?? item.materialId) !== processId || item.locked) return item;
          done[item.materialId] = completedAt;
          return { ...item, needsReread: false };
        }),
      }));
      set({ ...current, tracks, done });
      const finished = tracks.filter((track) => !isComplete(track, before) && isComplete(track, done));
      for (const track of finished) {
        trackTraining("training_track_completed", { track_id: track.id, item_count: track.items.length });
      }
      refreshWhenSettled();
      return finished;
    },
    [refreshWhenSettled, set],
  );

  const watched = useCallback(
    (materialId: string, trackId: string, progress: VideoProgress, viewerKey: string | null) => {
      const current = latest.current;
      // A departing watch session may finish reporting after a different account's list arrives.
      if (current.kind !== "ready" || current.viewerKey !== viewerKey) return;
      for (const during of progressDuring.current) during.set(materialId, progress);
      const before = current.done;
      // A tick still saving for it decides: its own answer will say what is stored.
      const applied = queues.current.has(materialId)
        ? { ...withProgress(current.tracks, before, materialId, { ...progress, completedAt: null }), done: before }
        : withProgress(current.tracks, before, materialId, progress);
      const { tracks, done, completed } = applied;
      // No generation bump: a re-read out now still lands, with this laid over it.
      set({ ...current, tracks, done });
      if (!completed) return;
      toast.success(MARKED_DONE);
      trackTraining("training_video_completed", { material_id: materialId, track_id: trackId });
      for (const track of newlyCompleted(tracks, materialId, before, done)) {
        trackTraining("training_track_completed", { track_id: track.id, item_count: track.items.length });
        celebrate(track);
      }
    },
    [set],
  );

  const signIn = useCallback(() => {
    // Both requests can come back 401 together; only one trip to sign-in.
    if (latest.current.kind !== "needs_signin" || isRedirecting()) return;
    const deps = browserDeps();
    if (!shouldRedirect(deps.now, deps.storage, TRAINING_SIGNIN_ATTEMPT_KEY)) {
      set({ kind: "signin_failed" });
      return;
    }
    beginSignIn(deps.returnTo, deps.storage, deps.navigate, deps.now, TRAINING_SIGNIN_ATTEMPT_KEY);
  }, [set]);

  return { state, retry, toggle, signIn, passed, acknowledged, refresh: refreshWhenSettled, watched };
}
